/* DeviceMultiEditViewComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./DeviceMultiEditViewRenderer");
const SiteCFUtility = require("../../../../lib/SiteCfUtility");
const jsonPatch = require("fast-json-patch");

const connectionInformationFields = [
    "ip",
    "userName",
    "password",
    "enablePassword",
    "port",
    "proxyList",
    "type",
];

// Mirrors the Settings > Custom Fields > Tags validation (see CustomFieldsComponent).
const MAX_TAG_NAME_LENGTH = 32;
const TAG_NAME_CHARSET_PATTERN = /^[a-zA-Z0-9 .\-_&@:()[\]]*$/;
const TAG_NAME_LENGTH_ERROR_MSG = (name) => `Tag name "${name}" must be 32 characters or fewer.`;
const TAG_NAME_CHARSET_ERROR_MSG = (name) =>
    `The Tag name "${name}" can only contain letters, numbers, spaces, ".", "-", "_", "&", "@", ":", "(", ")", "[", "]"`;

class DeviceMultiEditViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        userSession,
        DeviceEntity,
        CustomFieldsEntity,
        OsImageServerEntity,
        EngineZoneEntity,
        OrgZoneEntity,
        LicenseRestriction,
        MUF,
        solutionsManager,
        SiteEntity,
        ConnectionInfo,
        jnibAdapter,
        ExtendedPropType,
        ExtendedProps,
        Tags
    ) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = MUF;
        this._tabComponent = this._gameEditor.getTabCmpnt();

        this._userSession = userSession;
        this._DeviceEntityDef = DeviceEntity;
        this._CustomFieldsEntityDef = CustomFieldsEntity;
        this._osImageServerEntity = OsImageServerEntity;
        this._engineZoneEntity = EngineZoneEntity;
        this._orgZoneEntity = OrgZoneEntity;
        this._licenseRestriction = LicenseRestriction;
        this._connectionInfoEntityDef = ConnectionInfo;
        this._nibApiService = jnibAdapter.getNibApiService();
        this._isOSMSolutionAvailable = this._licenseRestriction.checkLicense("OSM");
        this._osImageServerFeed = [];
        this._connectionDropdownList = undefined;
        this._lockedConnectionMethods = [];
        this._connectionTypeDefs = undefined;
        this._engineZoneFeed = undefined;
        this._multipleConnectionMethods = false;
        this._controlledDevicesPresent = false;

        // EDP
        this._extendedPropTypeEntityDef = ExtendedPropType;
        this._extendedPropTypes = [];
        this._extendedPropsEntityDef = ExtendedProps;
        this._extendedPropList = {};

        this._siteEntityDef = SiteEntity;
        this._siteFeed = undefined;

        this._solutionDefinition = solutionsManager.getActiveSolution();
        this._solutionName = this._solutionDefinition.name || "";
        this._devices = undefined;
        this._customFieldColumnDef = undefined;
        this._inheritedCustomFields = [];
        this._devicesSupportedForBackup = true;

        this._siteCfUtility = new SiteCFUtility();

        this._TagsEntity = Tags;
        this._tags = undefined;
        this._tagOptions = undefined;
        this._tagOptionsLabelIdMap = undefined;
        const authorizedActions = _.get(userSession, "_userEntity.authorizedActions", []);
        this._canCreateTagsOnTheFly =
            authorizedActions.indexOf("customFieldsWrite") > -1 &&
            authorizedActions.indexOf("otfTagCreate") > -1;

        this._gameEditor.hideCabinetCpmnt();
        this._renderer = new Renderer(this);

        this._registerListeners();
    }

    get customFieldsJtypes() {
        return Object.entries(this._customFieldColumnDef)
            .filter(
                ([, customFieldDef]) =>
                    _.get(customFieldDef, "enabled") && !_.get(customFieldDef, "systemOwned")
            )
            .map(([customFieldId, customFieldDef]) =>
                Object.assign({id: customFieldId}, customFieldDef)
            )
            .sort(this._CustomFieldsEntityDef.getSortingComparator("order"))
            .map(this.gameEditor.generateCustomFieldJtype);
    }

    generateTagOptionsMap() {
        const labelIdMap = {};
        const options = (this._tags || []).map((tag) => {
            labelIdMap[tag.name] = tag.id;
            return {
                [tag.id]: tag.name,
            };
        });
        options.sort((a, b) => {
            const nameA = Object.values(a)[0];
            const nameB = Object.values(b)[0];
            return nameA.localeCompare(nameB);
        });
        return {options, labelIdMap};
    }

    _refreshTagOptions() {
        const {options, labelIdMap} = this.generateTagOptionsMap();
        this._tagOptions = options;
        this._tagOptionsLabelIdMap = labelIdMap;
    }

    get tagOptions() {
        return this._tagOptions || [];
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        this._tabComponent.on(
            "DirtyChanged",
            (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this))
        );
    }

    _unregisterListeners() {
        this._tabComponent.on(
            "DirtyChanged",
            (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this))
        );
    }

    _registerServerFeedListeners() {
        if (!_.isEmpty(this._osImageServerFeed)) {
            this._osImageServerFeed.on(
                "EntityPropertiesUpdated",
                (this._onOsImageServerFeedUpdated = this._onOsImageServerFeedUpdated.bind(this))
            );
        }
    }

    _unregisterServerFeedListeners() {
        this._osImageServerFeed.removeListener(
            "EntityPropertiesUpdated",
            this._onOsImageServerFeedUpdated
        );
    }

    _registerEngineZoneListeners() {
        this._engineZoneFeed.on(
            "EntityPropertiesUpdated",
            (this._onEngineZoneFeedUpdated = this._onEngineZoneFeedUpdated.bind(this))
        );
    }

    _unregisterEngineZoneFeedListeners() {
        this._engineZoneFeed.removeListener(
            "EntityPropertiesUpdated",
            this._onEngineZoneFeedUpdated
        );
    }

    _registerOrgZoneListeners() {
        this._orgZoneFeed.on(
            "EntityPropertiesUpdated",
            (this._onOrgZoneFeedUpdated = this._onOrgZoneFeedUpdated.bind(this))
        );
    }

    _unregisterOrgZoneFeedListeners() {
        this._orgZoneFeed.removeListener("EntityPropertiesUpdated", this._onOrgZoneFeedUpdated);
    }

    _registerSiteFeedListeners() {
        this._siteFeed.on(
            "EntityPropertiesUpdated",
            (this._onSiteFeedUpdated = this._onSiteFeedUpdated.bind(this))
        );
    }

    _unregisterSiteFeedListeners() {
        this._siteFeed.removeListener("EntityPropertiesUpdated", this._onSiteFeedUpdated);
    }

    _onEdpPristinePropertiesChanged() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get siteIds() {
        const siteIds = this.siteList.map((site) => site.id);
        return siteIds;
    }

    get sitePaths() {
        const sitePaths = this.siteList.map((site) => {
            let path = site.path || "/";
            path = path.replace("/" + this._userSession.activeOrganization.name, "");
            return path;
        });
        // sitePaths.unshift("/");
        return sitePaths;
    }

    get siteList() {
        return this._siteFeed.getCacheAsArray();
    }

    async shutdown() {
        this._unregisterListeners();
        // this._unregisterExtendedPropsListeners();

        // Object.values(this._extendedPropList).forEach(async (propTypeFeed) => {
        //     await propTypeFeed.shutdown();
        // });

        if (!_.isUndefined(this._osImageServerFeed) && !_.isEmpty(this._osImageServerFeed)) {
            this._unregisterServerFeedListeners();
        }
        if (!_.isUndefined(this._engineZoneFeed) && !_.isEmpty(this._engineZoneFeed)) {
            this._unregisterEngineZoneFeedListeners();
            await this._engineZoneFeed.shutdown();
        }
        if (!_.isUndefined(this._orgZoneFeed) && !_.isEmpty(this._orgZoneFeed)) {
            this._unregisterEngineZoneFeedListeners();
            await this._orgZoneEntity.shutdown;
        }
        if (!_.isUndefined(this._siteFeed) && !_.isEmpty(this._siteFeed)) {
            this._unregisterSiteFeedListeners();
            await this._siteFeed.shutdown();
        }

        return this._renderer.shutdown().then(
            () => {
                this._devices = undefined;
                this._logger = undefined;
                this._gameEditor = undefined;
                this._renderer = undefined;
                this._osImageServerEntity = undefined;
                this._engineZoneEntity = undefined;
                this._orgZoneFeed = undefined;
                this._licenseRestriction = undefined;
                this._isOSMSolutionAvailable = undefined;
                this._osImageServerFeed = undefined;
                this._connectionDropdownList = undefined;
                this._connectionTypeDefs = undefined;
                this._controlledDevicesPresent = undefined;
                this._multipleConnectionMethods = undefined;
                this._lockedConnectionMethods = undefined;
                this._inheritedCustomFields = undefined;
                this._deviceCategory = undefined;

                this._siteEntityDef = undefined;
                this._siteFeed = undefined;

                this._extendedPropTypeEntityDef = undefined;
                this._extendedPropTypes = undefined;
                this._extendedPropsEntityDef = undefined;
                this._extendedPropList = undefined;
            },
            (err) => {
                this._logger.warn(err, "DeviceDetailsViewComponent.shutdown() error.");
            }
        );
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onDirtyStateChanged(dirtyState) {
        this.emit("Dirty", dirtyState);

        let onBeforeUnloadFunc = () => {
            return this._modalUserFeedback
                .displayConfirmation("Would you like to Save or Discard your changes?", {
                    title: "Unsaved Changes",
                    confirmDetails: {
                        text: "Save",
                        type: "additive",
                    },
                    rejectDetails: {
                        text: "Discard",
                        type: "text",
                    },
                })
                .then(() => {
                    return this.saveDevice().then(_.noop); // it will not close the tab if it will have something in result
                })
                .catch((err) => {
                    if (err.userRejected) {
                        return this._modalUserFeedback.hideDisplayed().then(_.noop);
                    } else {
                        console.error("Error in onBeforeUnloadFunc! Err:", err);
                    }
                });
        };

        if (this.isDirty()) {
            this._gameEditor.setOnBeforeUnload(onBeforeUnloadFunc);
        } else {
            this._gameEditor.clearOnBeforeUnload();
        }
    }

    _onPendingPatchesChanged() {
        // TODO - this is a quick hack because the Array ref adapter does a shutdown and complete rebuild every time you call setValue. the effect to the user is they lose focus in the proxy list if they stop typing longer than the debounce
        // this.emit('DevicePropertiesChanged');
    }

    _onPristinePropertiesChanged(pristineProperties, before) {
        this.emit("DevicePropertiesChanged", pristineProperties, before);
    }

    _onOsImageServerFeedUpdated(/*entity, patches, props, feed*/) {
        //console.log(entity, patches, props, feed);
        this.emit("OsImageServerFeedUpdated");
        //this._renderer._renderDeviceDetails();
    }

    _onEngineZoneFeedUpdated(/*entity, patches, props, feed*/) {
        //console.log(entity, patches, props, feed);
        this.emit("EngineZoneFeedUpdate");
    }

    _onOrgZoneFeedUpdated(/*entity, patches, props, feed*/) {
        //console.log(entity, patches, props, feed);
        this.emit("OrgZoneFeedUpdate");
    }

    _onSiteFeedUpdated(/*entity, patches, props, feed*/) {
        //console.log(entity, patches, props, feed);
        this.emit("SiteFeedUpdated");
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    get editorMode() {
        return this.getEditorMode();
    }

    getEditorMode() {
        return this._gameEditor.getInstanceEditorStateProperty("details.editorMode") || "general";
    }

    set editorMode(editorState) {
        return this.setEditorMode(editorState);
    }

    setEditorMode(editorState, silent) {
        let old = this.getEditorMode();
        this._gameEditor.setInstanceEditorStateProperty("details.editorMode", editorState);
        if (!silent) {
            this.emit("EditorModeChanged", editorState, old);
        }
        return this;
    }

    isDirty() {
        return this._tabComponent.getDirty();
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get logger() {
        return this._logger;
    }

    get osImageServerList() {
        if (!_.isEmpty(this._osImageServerFeed)) {
            return this._osImageServerFeed.getCacheAsArray();
        }
        return [];
    }

    /** @deprecated osImageServerMainId */
    get osImageServerMasterId() {
        return this.osImageServerMainId;
    }

    get osImageServerMainId() {
        return _.get(
            this.osImageServerList.find((osImageServer) => osImageServer.isMain),
            "id"
        );
    }

    get osImageServerIds() {
        return this.osImageServerList.map((osImageServer) => osImageServer.id);
    }

    get osImageServerNames() {
        return this.osImageServerList.map((osImageServer) => osImageServer.name);
    }

    getDevicesRelatedImageServer() {
        let devicesRelatedImageServers = this._devices.map((device) =>
            this.getOsImageServerId(device.fileServerIdentifier)
        );
        let devicesRelatedImageServersUniq = _.uniq(devicesRelatedImageServers);
        if (devicesRelatedImageServersUniq.length === 1) {
            return devicesRelatedImageServersUniq[0];
        } else {
            return null;
        }
    }

    getOsImageServerId(identifier) {
        if (!identifier) {
            return;
        }
        return _.get(
            this.osImageServerList.find((osImageServer) => osImageServer.identifier === identifier),
            "id"
        );
    }

    get orgZoneList() {
        return this._orgZoneFeed.getCacheAsArray();
    }

    getOrgZoneByEngineZoneId(engineZoneId) {
        let selectedOrgZone = undefined;
        this.orgZoneList.forEach((orgZone) => {
            if (orgZone.engineZoneId === engineZoneId) {
                selectedOrgZone = orgZone;
            }
        });
        return selectedOrgZone;
    }

    /**
     * Get the engines that should be available in the drop down list.
     */
    get visibleEngineZoneList() {
        let filteredEngines = _.filter(this._engineZoneFeed.getCacheAsArray(), (engineZone) => {
            // If the orgZone is equal to the current device engineZone or
            // the orgZone is enabled. Add this to the visible engines.
            // We should always include the current device engineZone in the list.
            let orgZone = this.getOrgZoneByEngineZoneId(engineZone.id);
            return (
                this.devicesEngineZoneIds.indexOf(engineZone.id) !== -1 ||
                (orgZone && orgZone.zoneEnabled)
            );
        });
        return filteredEngines;
    }

    get engineZoneIds() {
        return this.visibleEngineZoneList.map((orgZone) => orgZone.id);
    }

    get devicesEngineZoneIds() {
        return this._devices.map((device) => device.engineZoneId);
    }

    get engineZones() {
        return this._engineZoneFeed.cache;
    }

    get zoneNames() {
        return this.visibleEngineZoneList.map((engineZone) => {
            let orgZone = this.getOrgZoneByEngineZoneId(engineZone.id);
            let displayName;
            if (orgZone) {
                displayName = engineZone.displayName;
                if (engineZone.currentState !== "ACTIVE") {
                    displayName += " (Offline)";
                }
                if (!orgZone.zoneEnabled) {
                    displayName += " (Disabled in system settings) - Please select another zone";
                }

                if (engineZone.currentState === "DELETED") {
                    displayName = "Deleted Engine";
                }
            } else {
                displayName = "Unknown Engine";
            }
            return displayName;
        });
    }

    getConnectionInfoJtype() {
        return this._DeviceEntityDef.connectionInformationJtype;
    }

    setTabDirty(state) {
        this._tabComponent.setDirty(state);
    }

    closeTab() {
        return this._gameEditor.closeEditor();
    }

    whenModelIsReady() {
        return Promise.all([
            this._CustomFieldsEntityDef.loadColumnDefinitions(
                this._userSession.activeOrganization.id
            ),
            this._gameEditor.whenDeviceIsReady(),
            this._connectionInfoEntityDef.buildJtypeSelectOptions(
                this._userSession.activeOrganization.id
            ),
            this._CustomFieldsEntityDef.loadCustomFields(this._userSession.activeOrganization.id),
            this._gameEditor._getSupportedOs(),
        ])
            .then(
                ([
                    customFieldColumnDef,
                    devicesEntities,
                    jtypeOptionsAndTypeDefs,
                    customFieldEty,
                ]) => {
                    this._deviceEntities = devicesEntities || [];

                    if (devicesEntities.length > 0) {
                        this._deviceCategory = devicesEntities[0].deviceRawCategory;
                    }

                    this._devices = this.extractDevicesData(devicesEntities);

                    this._customFieldsList = customFieldEty.fields;
                    this._inheritableCfs = Object.keys(customFieldColumnDef).filter(
                        (cf) => !customFieldColumnDef[cf].deviceOnly
                    );

                    let jtypeSelectOptions = jtypeOptionsAndTypeDefs.selectOptions;
                    // Fallback to cli default connection
                    if (_.isEmpty(_.get(jtypeSelectOptions, "connectionType@Select.options"))) {
                        this._connectionDropdownList = {
                            "connectionType@Select.options": {
                                cliConnection: this._DeviceEntityDef.connectionInformationJtype,
                                "cliConnection@Display.label": " CLI connection",
                            },
                        };
                    } else {
                        this._connectionDropdownList = jtypeSelectOptions;
                        this._connectionTypeDefs = jtypeOptionsAndTypeDefs.typeDefs;
                    }

                    let i = 0;
                    do {
                        if (this._devices[i].controlType === "CONTROLLED") {
                            // _controlledDevicesPresent doesn't include hybrid controlled devices on purpose
                            this._controlledDevicesPresent = true;
                        }

                        if (
                            this._devices[i].connectionMethodLocked === true &&
                            this._lockedConnectionMethods.indexOf(
                                this._devices[i].connectionMethod
                            ) === -1
                        ) {
                            this._lockedConnectionMethods.push(this._devices[i].connectionMethod);
                        }

                        i++;
                    } while (!this._controlledDevicesPresent && i < this._devices.length);

                    i = 1;
                    const firstConnectionMethod =
                        this._devices[0].connectionMethod || "cliConnection";
                    do {
                        if (
                            firstConnectionMethod !==
                            (this._devices[i].connectionMethod || "cliConnection")
                        ) {
                            this._multipleConnectionMethods = true;
                        }
                        i++;
                    } while (!this._multipleConnectionMethods && i < this._devices.length);

                    this._customFieldColumnDef = customFieldColumnDef;
                    /* @TODO - remove custom columns definitions for now
                const customFields = this._devices.getCustomFieldValues() || {};
                let updateRequired = false;

                Object.keys(customFieldColumnDef).forEach(customFieldId => {
                    let customField = customFields[customFieldId];

                    if (_.isUndefined(customField)) {
                        switch(customFieldColumnDef[customFieldId].dataType.toLowerCase()) {
                            case "boolean":
                                customFields[customFieldId] = false;
                                break;
                            case "string":
                                customFields[customFieldId] = "";
                                break;
                            case "number":
                                customFields[customFieldId] = null;
                                break;
                            default:
                                console.warn("Custom Field: Invalid data type for " + customFieldId + " - Field will not be added to device");
                        }
                        updateRequired = true;
                    }
                    else {
                        switch(customFieldColumnDef[customFieldId].dataType.toLowerCase()) {
                            case "boolean":
                                if (!_.isBoolean(customField)) {
                                    customField = (customField === true);
                                    updateRequired = true;
                                }
                                break;
                            case "string":
                                if (!_.isString(customField)) {
                                    customField = _.toString(customField);
                                    updateRequired = true;
                                }
                                break;
                            case "number":
                                if (!_.isNumber(customField)) {
                                    customField = _.isNaN(customField) ? null : _.toNumber(customField);
                                    updateRequired = true;
                                }
                                break;
                            default:
                                console.warn("Custom Field: Invalid data type for " + customFieldId + " - Field will not be added to device");
                        }
                    }
                });

                Object.keys(customFields).forEach(customFieldId => {
                    if (_.isUndefined(customFieldColumnDef[customFieldId])) {
                        delete customFields[customFieldId];
                        updateRequired = true;
                    }
                });

                if (updateRequired) {
                    // this._device.setCustomFieldValues(customFields, true);
                    this._device.setProperty("customFieldValues", customFields, true);
                }*/

                    return Promise.resolve();
                }
            )
            .then(() => this._getExtendedPropTypes())
            .then(() => {
                if (this._isOSMSolutionAvailable === true) {
                    return this._osImageServerEntity
                        .startAscendOsImageServersFeed(
                            this._userSession.activeOrganization.id,
                            {},
                            {
                                id: true,
                                name: true,
                                identifier: true,
                                isMain: true,
                            }
                        )
                        .whenInitialized();
                } else {
                    return Promise.resolve({});
                }
            })
            .then((osImageServerFeed) => {
                this._osImageServerFeed = osImageServerFeed;

                if (this._isOSMSolutionAvailable === true) {
                    this._registerServerFeedListeners();
                }
                // TODO Patch for devices with missing connectionInformation (whole object or props) -- To be removed once fixed on the back-end side
                /* @TODO - remove connection information for now
                const defaultConnectionInformation = {
                    "ip": "",
                    "userName": "",
                    "password": "",
                    "enablePassword": "",
                    "port": 22,
                    "proxyList": [],
                    "type": "ssh"
                };
                let connectionInformation = this._device.getConnectionInformation();

                if (_.isUndefined(connectionInformation)) {
                    this._device.setProperty("connectionInformation", defaultConnectionInformation, true);
                }
                else {
                    Object.keys(defaultConnectionInformation).map(key => {
                        if (!connectionInformation[key]) {
                            connectionInformation[key] = defaultConnectionInformation[key];
                        }
                    });
                    if (!_.isEqual(connectionInformation, this._device.getConnectionInformation())) {
                        this._device.setProperty("connectionInformation", connectionInformation, true);
                    }
                }*/
                return this._engineZoneEntity
                    .startAscendEngineZonesFeed(
                        this._userSession.activeOrganization.id,
                        {},
                        {
                            id: true,
                            currentState: true,
                            displayName: true,
                        }
                    )
                    .whenInitialized();
            })
            .then((engineZoneFeed) => {
                this._engineZoneFeed = engineZoneFeed;
                this._registerEngineZoneListeners();
                return this._orgZoneEntity
                    .startAscendOrgZonesFeed(this._userSession.activeOrganization.id, {}, {})
                    .whenInitialized();
            })
            .then((orgZoneFeed) => {
                this._orgZoneFeed = orgZoneFeed;
                this._registerOrgZoneListeners();

                return this._siteEntityDef
                    .startSitesFeed(
                        this._userSession.activeOrganization.id,
                        {},
                        {
                            id: true,
                            path: true,
                        },
                        {
                            path: 1,
                        }
                    )
                    .whenInitialized();
            })
            .then((siteFeed) => {
                this._siteFeed = siteFeed;
                this._registerSiteFeedListeners();
                return this._TagsEntity.loadTags(this._userSession.activeOrganization.id);
            })
            .then((tags) => {
                this._tags = tags;
                this._refreshTagOptions();
                return Promise.resolve();
            })
            .catch((err) => {
                if (err !== "devices are no longer exist") {
                    this._modalUserFeedback.displayError({
                        errorMsg: "This devices cannot be opened.",
                        errorCode: "",
                    });
                    this._logger.warn(err, "DeviceMultiEditComponent.whenModelIsReady() error");
                }
                return Promise.reject(err);
            });
    }

    extractDevicesData(devicesEntities = []) {
        return devicesEntities.reduce((devicesProperties, device) => {
            if (
                this._devicesSupportedForBackup &&
                this._deviceCategory !== "iot" &&
                this._gameEditor._supportedOsList.indexOf(device.getDetectedOs()) <= -1
            ) {
                this._devicesSupportedForBackup = false;
            }
            return devicesProperties.concat(_.set(device.getPristineProperties(), "id", device.id));
        }, []);
    }

    isDeviceFormValid(dataToSave) {
        const dropdowns = [
                "siteId",
                "managementState",
                "environment",
                "detectedTypeBase",
                "engineZoneId",
                "fileServerId",
            ],
            inputChanges = dataToSave,
            isInvalidInputChanges = !_.isPlainObject(inputChanges) || _.isEmpty(inputChanges),
            isInputNameEmpty = _.has(inputChanges, "name") && inputChanges.name.trim().length === 0;

        dropdowns.forEach((field) => {
            if (_.has(inputChanges, field) && _.isEmpty(inputChanges[field])) {
                throw this._errorObj(field);
            }
        });

        if (isInvalidInputChanges) {
            throw {
                errorMsg: "Data is invalid. Please enter a valid data name.",
                errorCode: "SyntaxError",
                title: "Error",
            };
        }

        if (isInputNameEmpty) {
            throw {
                errorMsg: "Name cannot be left empty. Please enter a valid device name.",
                errorCode: "SyntaxError",
                title: "Error",
            };
        }

        return true;
    }

    _errorObj(field) {
        return {
            errorMsg: `Data is invalid. Please enter a valid ${field} value.`,
            errorCode: "SyntaxError",
            title: "Error",
        };
    }

    updateDevices() {
        const ids = this._devices.map((device) => device.id);
        return this._gameEditor._initMultipleDevices(ids).then((devicesEntities) => {
            this._devices = this.extractDevicesData(devicesEntities);
            return this._devices;
        });
    }

    // Runs before saveDevice()'s operations are built so a tag-creation failure rejects the whole save.
    _resolveNewTagIds(dataToSave) {
        const tagsValue = dataToSave.tagIds;
        if (!tagsValue || !Array.isArray(tagsValue.tagIds) || !tagsValue.tagIds.length) {
            return Promise.resolve();
        }
        const newTags = [],
            existingTags = [];
        tagsValue.tagIds.forEach((tag) => {
            if (tag.search(/^__new__/) >= 0) {
                newTags.push(tag);
            } else {
                existingTags.push(tag);
            }
        });
        if (!newTags.length) {
            return Promise.resolve();
        }
        const newTagNames = newTags.map((tag) => tag.replace(/^__new__/, ""));
        this._validateNewTagNames(newTagNames);
        return Promise.all(
            newTagNames.map((name) => {
                const tagEntity = new this._TagsEntity(this._userSession.activeOrganization.id);
                tagEntity.name = name;
                return tagEntity.create();
            })
        ).then((createdTags) => {
            this._tags = this._tags.concat(createdTags);
            this._refreshTagOptions();
            tagsValue.tagIds = existingTags.concat(createdTags.map((tag) => tag.id));
        });
    }

    /**
     * Applies the same name-length/charset rules as Settings > Custom Fields > Tags
     * to tags created on the fly, before they're sent to the server.
     * @param {string[]} names
     * @throws {{errorMsg: string, errorCode: string, title: string}}
     */
    _validateNewTagNames(names) {
        const invalidLength = names.find((name) => name.length > MAX_TAG_NAME_LENGTH);
        if (invalidLength) {
            throw {
                errorMsg: TAG_NAME_LENGTH_ERROR_MSG(invalidLength),
                errorCode: "SyntaxError",
                title: "Error",
            };
        }
        const invalidCharset = names.find((name) => !TAG_NAME_CHARSET_PATTERN.test(name));
        if (invalidCharset) {
            throw {
                errorMsg: TAG_NAME_CHARSET_ERROR_MSG(invalidCharset),
                errorCode: "SyntaxError",
                title: "Error",
            };
        }
    }

    async saveDevice(formData, pristineConnectionObject, deletePathsMap) {
        const tenantId = this._gameEditor._activeOrgId;
        let fileServerId,
            connectionInformation,
            deviceConnectionInformation,
            pristineConnectionInformation,
            connectionInformationPendingPatches,
            engineZoneId,
            zoneChange,
            siteIdChange,
            siteId,
            fileServerChange,
            operations = [],
            relUpdateRequired = false,
            dataToSave = _.isUndefined(formData) ? this._renderer._getFormValue() : formData,
            operationsPerDevice = 1;

        const deepMerge = (target, source) => {
            const output = Object.assign({}, target);
            for (const key in source) {
                if (source.hasOwnProperty(key)) {
                    if (
                        typeof source[key] === "object" &&
                        source[key] !== null &&
                        !Array.isArray(source[key])
                    ) {
                        output[key] = deepMerge(target[key] || {}, source[key]);
                    } else {
                        output[key] = source[key];
                    }
                }
            }
            return output;
        };

        if (!this.isDeviceFormValid(dataToSave)) {
            return Promise.resolve();
        }

        await this._resolveNewTagIds(dataToSave);

        this._inheritedCustomFields = this._siteCfUtility.getInheritedCfs();

        // Workaround to handle connectionInformation fields and custom fields
        for (let key in dataToSave) {
            if (key === "restoreCommands") {
                dataToSave[key] = dataToSave[key] ? dataToSave[key].split("\n") : [];
            }
            if (key === "tagIds") {
                const tagIdsValue = dataToSave.tagIds;
                delete dataToSave.tagIds;
                if (Array.isArray(tagIdsValue)) {
                    // No action picker shown for this user (createOption disabled) - full replace.
                    dataToSave.tagIds = tagIdsValue;
                } else {
                    const {action, tagIds} = tagIdsValue;
                    if (action === "Add") {
                        dataToSave.addTagIds = tagIds;
                    } else if (action === "Remove") {
                        dataToSave.removeTagIds = tagIds;
                    } else if (action === "Replace") {
                        dataToSave.tagIds = tagIds;
                    }
                }
            }
            if (key === "connectionObject") {
                dataToSave.connectionMethod = _.get(
                    dataToSave,
                    "connectionObject.connectionType",
                    "cliConnection"
                );
                // connectionInformation = _.omit(dataToSave.connectionObject, "connectionType");
                // pristineConnectionInformation = _.omit(pristineConnectionObject, "connectionType");
                // connectionInformationPendingPatches = jsonPatch.compare(
                //     pristineConnectionInformation,
                //     connectionInformation
                // );
                // delete dataToSave[key];
            }

            if (connectionInformationFields.includes(key)) {
                dataToSave["connectionInformation." + key] = dataToSave[key];
                delete dataToSave[key];
            }

            if (key === "customFieldValues") {
                const customFieldValues = dataToSave.customFieldValues;
                const customFieldUuids = Object.keys(this._customFieldColumnDef);
                for (let uuid in customFieldValues) {
                    if (customFieldUuids.includes(uuid)) {
                        dataToSave["customFieldValues." + uuid] = customFieldValues[uuid];
                        dataToSave["customFieldSources." + uuid] = null;
                    }
                    // console.log(this._inheritedCustomFields);
                }
                this._inheritedCustomFields.forEach((cf) => {
                    // delete dataToSave["customFieldSources." + cf];
                    dataToSave["customFieldSources." + cf] = {};
                });
                delete dataToSave.customFieldValues;
                this._siteCfUtility.clearInheritedCfs();
            }

            if (key === "siteId") {
                if (formData[key] === "/") {
                    dataToSave["siteSetByUser"] = false;
                } else {
                    dataToSave["siteSetByUser"] = true;
                }
            }

            if (key === "discoveryLevel") {
                if (
                    _.get(this._renderer, "_discoveryLevelInheritanceUtility._isInherited", false)
                ) {
                    dataToSave["discoveryLevelFrom"] = "_Site";
                    dataToSave["discoveryLevel"] = null;
                } else {
                    dataToSave["discoveryLevelFrom"] = null;
                }
            }

            // Removing file server relationship which is processed separately
            if (key === "fileServerId") {
                fileServerId = dataToSave[key];
                relUpdateRequired = true;
                fileServerChange = true;
                operationsPerDevice++;
                delete dataToSave[key];
            } else if (key === "engineZoneId") {
                engineZoneId = dataToSave[key];
                zoneChange = true;
                relUpdateRequired = true;
                operationsPerDevice++;
                delete dataToSave[key];
            } else if (key === "siteId") {
                siteId = dataToSave[key];
                siteIdChange = true;
                operationsPerDevice++;
                relUpdateRequired = true;
                delete dataToSave[key];
            }
        }

        this._modalUserFeedback.displayLoading({
            message: "Updating devices... This could take a moment",
        });

        const maxSize = 500;
        const selectedUpdates = new Array(Math.floor(operations.length / maxSize));
        const selectedEdpUpdates = new Array(Math.floor(operations.length / maxSize));
        let totalProgressCount = 0;
        let lastCallProgressCount = 0;

        const showProgressMessage = (progressCallBack) => {
            let progressCount = _.get(progressCallBack, "progressCount", 0);
            let deviceCount = Math.floor(operations.length / operationsPerDevice);

            if (progressCount >= lastCallProgressCount) {
                totalProgressCount += progressCount - lastCallProgressCount;
            } else {
                totalProgressCount += progressCount;
            }
            lastCallProgressCount = progressCount;

            if (Math.floor(totalProgressCount / operationsPerDevice) <= deviceCount) {
                this._modalUserFeedback.showLoadingProgress(
                    Math.floor(totalProgressCount / operationsPerDevice) +
                        " of " +
                        deviceCount +
                        " device" +
                        (deviceCount > 1 ? "s" : "")
                );
            }
        };

        let workOperation;
        const edpOperations = [];
        this._devices.forEach((device) => {
            this._extendedPropList[device.id].forEach((extendedProp) => {
                const formExtendedProp = dataToSave[extendedProp.getExtensionType()];
                if (formExtendedProp) {
                    const mergedEdpInstance = deepMerge(
                        extendedProp.getEdpInstance(),
                        formExtendedProp
                    );
                    // Delete paths that are not currently selected in the form
                    // (e.g. unselected options from select_options, choice_options,
                    // complete_options, complete_all_type controls)
                    const deletePaths =
                        (deletePathsMap && deletePathsMap[extendedProp.getExtensionType()]) || [];
                    deletePaths.forEach((pathStr) => {
                        if (_.has(mergedEdpInstance, pathStr)) {
                            const pathSegments = pathStr.split(".");
                            const targetProp = pathSegments.pop();
                            let targetObject = mergedEdpInstance;
                            if (pathSegments.length > 0) {
                                targetObject = _.get(mergedEdpInstance, pathSegments.join("."));
                            }
                            if (targetObject) {
                                delete targetObject[targetProp];
                            }
                        }
                    });
                    edpOperations.push({
                        actionType: "entity",
                        action: "update",
                        entityType: "ExtendedProps",
                        entityId: extendedProp.id,
                        properties: {
                            edpInstance: mergedEdpInstance,
                        },
                    });
                }
            });

            if (dataToSave.connectionObject) {
                deviceConnectionInformation = _.cloneDeep(device.connectionInformation);
                connectionInformation = _.omit(dataToSave.connectionObject, [
                    "connectionType",
                    "ip",
                ]);
                pristineConnectionInformation = _.omit(deviceConnectionInformation, [
                    "connectionType",
                    "ip",
                ]);
                if (!connectionInformation.proxyList) {
                    pristineConnectionInformation = _.omit(
                        pristineConnectionInformation,
                        "proxyList"
                    );
                }
                connectionInformationPendingPatches = jsonPatch.compare(
                    pristineConnectionInformation,
                    connectionInformation
                );
                if (
                    connectionInformationPendingPatches &&
                    connectionInformationPendingPatches.length > 0
                ) {
                    // setting enablePassword to an object for applyPatch to work
                    if (
                        connectionInformationPendingPatches.some(
                            (val) => val.path && val.path.search("enablePassword") >= 0
                        )
                    ) {
                        if (!deviceConnectionInformation.enablePassword) {
                            deviceConnectionInformation.enablePassword = {};
                        }
                    }
                    jsonPatch.applyPatch(
                        deviceConnectionInformation,
                        connectionInformationPendingPatches
                    );
                    dataToSave.connectionInformation = deviceConnectionInformation;
                }
            }

            operations.push({
                actionType: "entity",
                action: "update",
                entityType: "Device",
                entityId: device.id,
                properties: _.cloneDeep(_.omit(dataToSave, "connectionObject")),
            });

            if (relUpdateRequired) {
                if (fileServerChange) {
                    if (fileServerId) {
                        operations.push({
                            actionType: "rel",
                            action: "set",
                            lEntityType: "Device",
                            lEntityId: device.id,
                            relName: "imageServer",
                            rEntityType: "OsImageServer",
                            rEntityId: fileServerId,
                        });
                    } else {
                        operations.push({
                            actionType: "rel",
                            action: "remove",
                            lEntityType: "Device",
                            lEntityId: device.id,
                            relName: "imageServer",
                            rEntityType: "OsImageServer",
                            rEntityId: this.getOsImageServerId(device.fileServerIdentifier),
                        });
                    }
                }
                if (zoneChange) {
                    if (engineZoneId) {
                        operations.push({
                            actionType: "rel",
                            action: "set",
                            lEntityType: "Device",
                            lEntityId: device.id,
                            relName: "engineZone",
                            rEntityType: "EngineZone",
                            rEntityId: engineZoneId,
                        });
                    } else {
                        operations.push({
                            actionType: "rel",
                            action: "remove",
                            lEntityType: "Device",
                            lEntityId: device.id,
                            relName: "engineZone",
                            rEntityType: "EngineZone",
                            rEntityId: device.engineZoneId,
                        });
                    }
                }

                if (siteIdChange) {
                    if (siteId && siteId !== "/") {
                        operations.push({
                            actionType: "rel",
                            action: "set",
                            lEntityType: "Device",
                            lEntityId: device.id,
                            rEntityId: siteId,
                            rEntityType: "Site",
                            relName: "site",
                            options: {skipHooks: ["countSiteDevices", "initSiteRel"]},
                        });
                    } else {
                        operations.push({
                            actionType: "rel",
                            action: "clear",
                            lEntityType: "Device",
                            lEntityId: device.id,
                            relName: "site",
                            rEntityType: "Site",
                            options: {skipHooks: ["countSiteDevices", "initSiteRel"]},
                        });
                    }
                    workOperation = workOperation || {
                        actionType: "runWork",
                        workName: "CountSiteDevices",
                    };
                }
            }
        });

        operations.forEach((selection, idx) => {
            const i = Math.floor(idx / maxSize);
            if (!selectedUpdates[i]) {
                selectedUpdates[i] = [];
            }
            selectedUpdates[i].push(selection);
        });

        edpOperations.forEach((selection, idx) => {
            const i = Math.floor(idx / maxSize);
            if (!selectedEdpUpdates[i]) {
                selectedEdpUpdates[i] = [];
            }
            selectedEdpUpdates[i].push(selection);
        });

        // const bulkUpdate = this._gameEditor._DeviceEntity.doBulk(
        //     tenantId,
        //     operations,
        //     showProgressMessage,
        //     100
        // );

        const sleep = () => {
            return new Promise((resolve) => {
                return setTimeout(resolve, 3000);
            });
        };

        const promises = [];
        selectedUpdates.forEach((currentBatch) => {
            if (workOperation) {
                currentBatch.push(workOperation);
            }
            promises.push(() =>
                this._gameEditor._DeviceEntity.doBulk(
                    tenantId,
                    currentBatch,
                    showProgressMessage,
                    100
                )
            );
            promises.push(sleep);
        });

        selectedEdpUpdates.forEach((currentBatch) => {
            promises.push(() =>
                this._extendedPropsEntityDef.doBulk(
                    tenantId,
                    currentBatch,
                    showProgressMessage,
                    100
                )
            );
            promises.push(sleep);
        });

        return promises
            .reduce((accumulatorPromise, nextPromise) => {
                return accumulatorPromise.then(() => {
                    return nextPromise();
                });
            }, Promise.resolve())
            .then(() => this.loadRelatedExtendedPropTypeData())
            .then((/*result*/) => {
                /* TODO Commenting until a proper summary is returned by doBulkAsProm()
                let userNotificationMessage = `Total number of devices: ${result.length || 0}`;
                return this._modalUserFeedback.displayNotification(userNotificationMessage, {title: 'Multi Edit Save Completed'});
                */
                this.setTabDirty(false);
                this._siteCfUtility.clearInheritedCfs();
                this._inheritedCustomFields = [];
                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn({error: err}, "DeviceDetailsViewComponent.saveDevice() error.");

                if (_.get(err, "errorCode") === "EntityInteractionsError") {
                    this.setTabDirty(false);
                }
                return err;
            })
            .then((error) => {
                return this.updateDevices().then((devices) => {
                    if (!devices.length) {
                        error = {
                            errorMsg:
                                "The selected devices have all been deleted by another user before changes could be saved. This form is now going to be closed.",
                            errorCode: "EntityInteractionsError",
                            title: "Error",
                        };
                    }
                    return error && Promise.reject(error);
                });
            });
    }

    _findReadOnlyPropertiesDeep(obj) {
        const readOnlyProps = [];
        const recurse = (node) => {
            if (!_.isPlainObject(node)) {
                return;
            }
            for (const [key, value] of Object.entries(node)) {
                if (key.endsWith("@Display.readOnly") && value === true) {
                    const propName = key.replace("@Display.readOnly", "");
                    readOnlyProps.push(propName);
                }
                if (_.isPlainObject(value)) {
                    recurse(value);
                }
            }
        };
        recurse(obj);
        return readOnlyProps;
    }

    async loadRelatedExtendedPropTypeData() {
        await this._deviceEntities.forEach(async (_device) => {
            this._extendedPropList[_device.id] = await _device.loadRelatedEdpData({
                enabled: true,
            });
            // await this._extendedPropList[_device.id].whenInitialized();
        });
    }

    async _getExtendedPropTypes() {
        await this.loadRelatedExtendedPropTypeData();

        const allExtendedPropTypeIds = [];
        Object.values(this._extendedPropList).forEach((propList) => {
            const propTypeIds = [];
            propList.forEach((propType) => {
                propTypeIds.push(propType.getExtendedPropsTypeId());
            });
            allExtendedPropTypeIds.push(propTypeIds);
        });

        // this._registerExtendedPropsListeners();
        this._extendedPropTypes = await this._extendedPropTypeEntityDef.loadExtendedPropTypes(
            this._userSession.activeOrganization.id,
            {
                $and: [
                    {id: {$in: _.intersection(...allExtendedPropTypeIds)}},
                    {deviceDetailsDisabled: {$ne: true}},
                    {"metadata.deviceDetailsDisabled": {$ne: true}},
                ],
            }
        );
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = DeviceMultiEditViewComponent;

DeviceMultiEditViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.userSession",
    "$teve.entities.Device",
    "$teve.entities.CustomFields",
    "$teve.entities.OsImageServer",
    "$teve.entities.EngineZone",
    "$teve.entities.OrgZone",
    "$teve.licenseRestriction",
    "$teve.ui.userFeedback",
    "$teve.solutionsManager",
    "$teve.entities.Site",
    "$teve.entities.ConnectionInfo",
    "$teve.jnibAdapter",
    "$teve.entities.ExtendedPropType",
    "$teve.entities.ExtendedProps",
    "$teve.entities.Tags",
];

DeviceMultiEditViewComponent.$displayDetails = {
    type: "View",
    name: "Device Multi-Edit",
    icon: "common.edit",
    shown: false,
    title: "MultiEdit",
    order: 1,
};
